import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Briefcase, ExternalLink, AlertTriangle, Loader2
} from 'lucide-react';
import Icon3D from '../components/Icon3D';
import { getJobBoards, trackJobBoardClick } from '../api/endpoints';

// Fallback boards shown when API is unavailable or user is not logged in
const FALLBACK_BOARDS = [
  { name: 'Seek Healthcare', url: 'https://www.seek.com.au/healthcare-jobs', description: 'Australia\'s largest job board with extensive healthcare listings', domain: 'seek.com.au' },
  { name: 'HealthcareLink', url: 'https://www.healthcarelink.com.au/', description: 'Specialised healthcare recruitment platform', domain: 'healthcarelink.com.au' },
  { name: 'Indeed Healthcare AU', url: 'https://au.indeed.com/Healthcare-jobs', description: 'Wide range of healthcare positions', domain: 'indeed.com' },
  { name: 'Medrecruit', url: 'https://www.medrecruit.com.au/', description: 'Medical recruitment specialists', domain: 'medrecruit.com.au' },
];

const JobsPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const tenantName = tenant?.name || 'MedMCQ';

  const [boards, setBoards] = useState([]);
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    document.title = `Healthcare Jobs | ${tenantName}`;
  }, [tenantName]);

  // Fetch job boards from backend (filtered by user's country)
  useEffect(() => {
    let cancelled = false;
    const fetchBoards = async () => {
      if (!user) {
        // Not logged in — show fallback
        setBoards(FALLBACK_BOARDS);
        setCountry('Australia');
        setUsingFallback(true);
        setLoading(false);
        return;
      }
      try {
        const res = await getJobBoards();
        if (cancelled) return;
        const data = res.data;
        if (data.boards && data.boards.length > 0) {
          setBoards(data.boards);
          setCountry(data.country || '');
          setUsingFallback(false);
        } else {
          // API returned empty — use fallback
          setBoards(FALLBACK_BOARDS);
          setCountry(data.country || user?.country || 'Australia');
          setUsingFallback(true);
        }
      } catch {
        if (cancelled) return;
        setBoards(FALLBACK_BOARDS);
        setCountry('Australia');
        setUsingFallback(true);
      }
      setLoading(false);
    };
    fetchBoards();
    return () => { cancelled = true; };
  }, [user]);

  const handleBoardClick = (board) => {
    if (board.id) {
      // Fire-and-forget click tracking for API-sourced boards
      trackJobBoardClick(board.id).catch(() => {});
    }
  };

  // Extract domain from URL for favicon
  const getDomain = (board) => {
    if (board.domain) return board.domain;
    try {
      return new URL(board.url).hostname;
    } catch {
      return '';
    }
  };

  // Use affiliate_url if present, otherwise fall back to url
  const getBoardUrl = (board) => board.affiliate_url || board.url;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Icon3D icon={Briefcase} colour="amber" size="md" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Healthcare Jobs</h1>
              <p className="text-slate-500">
                {country ? `Find medical and healthcare opportunities in ${country}` : 'Find medical and healthcare opportunities'}
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">External Job Listings Disclaimer</h3>
              <p className="text-sm text-amber-800">
                The job boards and listings linked below are external third-party websites. {tenantName} does not guarantee the accuracy,
                completeness, or reliability of any job listings or information provided on these external sites. We are not responsible
                for the content, hiring practices, or any outcomes resulting from your interaction with these platforms.
                Always verify opportunities directly with employers and exercise due diligence when applying for positions.
                Some job board links may be affiliate links. This does not affect the job listings shown or their order.
              </p>
            </div>
          </div>
        </div>

        {/* Job Boards Section */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-cyan-600" />
            {country ? `Job Boards — ${country}` : 'Job Boards'}
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading job boards...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {boards.map((board, index) => {
                const domain = getDomain(board);
                return (
                  <a
                    key={board.id || index}
                    href={getBoardUrl(board)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleBoardClick(board)}
                    className="bg-white rounded-xl border border-slate-200 p-5 hover:border-cyan-300 hover:shadow-md transition-all group"
                    style={{ boxShadow: 'var(--shadow-card)' }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {domain && (
                          <img
                            src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                            alt={`${board.name} logo`}
                            className="w-6 h-6 rounded object-contain flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <h3 className="font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                          {board.name}
                        </h3>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-cyan-500 flex-shrink-0" />
                    </div>
                    <p className="text-sm text-slate-500">{board.description}</p>
                  </a>
                );
              })}
            </div>
          )}

          {usingFallback && !loading && (
            <p className="text-xs text-slate-400 mt-3">
              Showing default boards. Log in to see job boards personalised for your country.
            </p>
          )}
        </div>

        {/* Tips Section */}
        <div className="rounded-xl p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)' }}>
          <h3 className="font-bold text-lg mb-4 relative z-10">Job Search Tips for Medical Graduates</h3>
          <ul className="space-y-2 text-cyan-100 relative z-10">
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              Keep your CV and cover letter tailored to each position
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              Register with multiple job boards to maximise opportunities
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              Network with colleagues and attend medical conferences
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              Research hospitals and health networks before applying
            </li>
            <li className="flex items-start gap-2">
              <span className="text-white">•</span>
              Verify registration requirements for your target country
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default JobsPage;
